/**
 * Variant Selector Component
 *
 * @package Enterprise\BookingSystem\Blocks
 */

/**
 * WordPress dependencies
 */
import { useDispatch, useSelect } from '@wordpress/data';
import { useEffect } from '@wordpress/element';

/**
 * Internal dependencies
 */
import type { ProductVariant, VariantFeature } from '../../../services/api/product-api.service';
import { store as addToCartStore } from '../../../data/add-to-cart';

type VariantSelectorProps = {
	readonly productId: number;
};

export const VariantSelector = ({ productId }: VariantSelectorProps) => {
	const dispatch = useDispatch(addToCartStore);

	const { isLoadingVariants, selectedVariantId, variantError, variants } = useSelect(
		(select) => {
			const storeSelectors = select(addToCartStore);
			return {
				isLoadingVariants: storeSelectors.isLoadingVariants(productId),
				selectedVariantId: storeSelectors.getSelectedVariantId(productId),
				variantError: storeSelectors.getValidationErrors(productId).variant,
				variants: storeSelectors.getVariants(productId),
			};
		},
		[productId],
	);

	useEffect(() => {
		void dispatch.loadVariants(productId);
	}, [productId, dispatch]);

	const handleVariantChange = (variantId: number) => {
		void dispatch.selectVariant(productId, variantId);
	};

	if (isLoadingVariants) {
		return (
			<div className="ebs-variant-selector ebs-variant-selector--loading">
				<p className="ebs-variant-selector__label">Loading packages...</p>
			</div>
		);
	}

	if (variants.length === 0) {
		return (
			<div className="ebs-variant-selector ebs-variant-selector--empty">
				<p className="ebs-variant-selector__empty-message">
					No packages available for this product.
				</p>
			</div>
		);
	}

	return (
		<div className="ebs-variant-selector">
			<label className="ebs-variant-selector__label" htmlFor={`variant-${productId}`}>
				Choose Package *
			</label>

			<div className="ebs-variant-selector__grid">
				{variants.map((variant: ProductVariant) => {
					const isSelected = variant.id === selectedVariantId;

					return (
						<button
							key={variant.id}
							type="button"
							className={`ebs-variant-selector__option${isSelected ? ' ebs-variant-selector__option--selected' : ''}`}
							onClick={() => {
								handleVariantChange(variant.id);
							}}
							aria-pressed={isSelected}
						>
							<div className="ebs-variant-selector__option-header">
								<span className="ebs-variant-selector__option-name">{variant.name}</span>

								{variant.features.length > 0 ? (
									<span
										className="ebs-variant-selector__info"
										onClick={(e) => { e.stopPropagation(); }}
										aria-label={`${variant.name} features`}
									>
										<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className="ebs-variant-selector__info-icon">
										<g clipPath="url(#clip0_1004_62385)">
											<path d="M10.0013 18.3333C14.6037 18.3333 18.3346 14.6024 18.3346 9.99999C18.3346 5.39762 14.6037 1.66666 10.0013 1.66666C5.39893 1.66666 1.66797 5.39762 1.66797 9.99999C1.66797 14.6024 5.39893 18.3333 10.0013 18.3333Z" stroke="#EF3F69" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
											<path d="M10 13.3333V9.99999M10 6.66666H10.0083" stroke="#EF3F69" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
										</g>
										<defs>
											<clipPath id="clip0_1004_62385">
											<rect width="20" height="20" fill="white"/>
											</clipPath>
										</defs>
										</svg>
										<span className="ebs-variant-selector__tooltip" role="tooltip">
											<span className="ebs-variant-selector__tooltip-title">Comes with:</span>
											<ul className="ebs-variant-selector__tooltip-list">
												{variant.features.map((feature: VariantFeature) => (
													<li key={feature.id} className="ebs-variant-selector__tooltip-item">
														<svg
															className="ebs-variant-selector__tooltip-check"
															xmlns="http://www.w3.org/2000/svg"
															viewBox="0 0 24 24"
															width="14"
															height="14"
															aria-hidden="true"
															fill="currentColor"
														>
															<path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
														</svg>
														{feature.name}
													</li>
												))}
											</ul>
										</span>
									</span>
								) : null}
							</div>
							<p className="ebs-variant-selector__option-meta">
								{variant.duration_hours > 0 ? (
									<span className="ebs-variant-selector__option-duration">
										{variant.duration_hours}{' '}
										{variant.duration_hours === 1 ? 'hour' : 'hours'}
									</span>
								) : null}
								{variant.duration_hours > 0 ? (
									<span className="ebs-variant-selector__option-sep"> – </span>
								) : null}
								<span className="ebs-variant-selector__option-price">
									{variant.formatted_price}
								</span>
							</p>
						</button>
					);
				})}
			</div>

			{variantError ? (
				<p className="ebs-variant-selector__error" role="alert">
					{variantError}
				</p>
			) : null}
		</div>
	);
};
